import React, { useEffect, useRef, useCallback } from "react"
import { Link } from "react-router-dom"
import { useLocation } from "react-router-dom"
import SimpleBar from "simplebar-react"
import { createMetisMenu, disposeMetisMenu } from "helpers/metisMenu"
import { useTranslation } from "react-i18next"
import { ROUTES } from "constants/routes"
import SuperAdminSidebarContent from "./SuperAdminSidebarContent"
import { resolveSuperAdminMenuPath } from "./superadminNav"
import usePermissionRefresh from "hooks/usePermissionRefresh"

import wuloojMark from "assets/images/superadmin/wulooj-mark.png"
import wuloojLogoFull from "assets/images/superadmin/wulooj-logo-full.png"

const MENU_ID = "superadmin-side-menu"

const getDirectSubMenu = parentNode => {
  if (!parentNode) return null
  return Array.from(parentNode.children || []).find(
    node =>
      node?.tagName?.toLowerCase() === "ul" &&
      node.classList?.contains("sub-menu")
  )
}

/**
 * Vertical sidebar for platform operators only — separate shell from tenant {@link SidebarContent}
 * so you can theme it independently (see `_superadmin-sidebar.scss`).
 */
const SuperAdminSidebar = () => {
  const { t } = useTranslation()
  const path = useLocation()
  const ref = useRef()

  usePermissionRefresh()

  const activateParentDropdown = useCallback(item => {
    if (!item) return
    item.classList.add("active")
    const parent = item.parentElement
    const parent2El = getDirectSubMenu(parent)

    if (parent2El && parent2El.id !== MENU_ID) {
      parent2El.classList.add("mm-show")
    }

    if (parent) {
      parent.classList.add("mm-active")
      const parent2 = parent.parentElement

      if (parent2) {
        parent2.classList.add("mm-show")

        const parent3 = parent2.parentElement

        if (parent3) {
          parent3.classList.add("mm-active")
          if (parent3.firstElementChild) {
            parent3.firstElementChild.classList.add("mm-active")
          }
        }
      }
    }
  }, [])

  const highlightActive = useCallback(() => {
    const ul = document.getElementById(MENU_ID)
    if (!ul) return

    const targetPath = resolveSuperAdminMenuPath(path.pathname)

    ul.querySelectorAll("a.active, a.mm-active").forEach(node => {
      node.classList.remove("active")
      node.classList.remove("mm-active")
    })
    ul.querySelectorAll("li.mm-active").forEach(node => {
      node.classList.remove("mm-active")
    })
    ul.querySelectorAll("ul.mm-show").forEach(node => {
      node.classList.remove("mm-show")
    })

    let matchingMenuItem = null
    const items = ul.getElementsByTagName("a")
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      const href = item.getAttribute("href") || ""
      const isToggleLink = item.classList.contains("has-arrow")
      const isHashLink = href === "#" || href === "/#"
      if (isToggleLink || isHashLink) continue

      const normalizedItemPath =
        (item.pathname || href).replace(/\/+$/, "") || "/"
      if (normalizedItemPath === targetPath) {
        matchingMenuItem = item
        break
      }
    }

    if (matchingMenuItem) {
      activateParentDropdown(matchingMenuItem)
    }
  }, [path.pathname, activateParentDropdown])

  useEffect(() => {
    const menuEl = document.getElementById(MENU_ID)
    const metis = menuEl ? createMetisMenu(menuEl) : null
    ref.current?.recalculate?.()
    return () => disposeMetisMenu(metis)
  }, [])

  useEffect(() => {
    highlightActive()
    const ul = document.getElementById(MENU_ID)
    const targetPath = resolveSuperAdminMenuPath(path.pathname)
    const el = ul?.querySelector?.(`a[href="${targetPath}"]`)
    if (el && ref.current?.getScrollElement) {
      const top = el.offsetTop
      if (top > window.innerHeight) {
        ref.current.getScrollElement().scrollTop = top - 300
      }
    }
  }, [highlightActive, path.pathname])

  return (
    <div
      className="vertical-menu vertical-menu--platform"
      data-sidebar-context="platform"
    >
      <div className="navbar-brand-box navbar-brand-box--platform">
        <Link to={ROUTES.SA_DASHBOARD} className="logo logo-dark">
          <span className="logo-sm">
            <img src={wuloojMark} alt="WULOOJ" height="22" />
          </span>
          <span className="logo-lg">
            <img src={wuloojLogoFull} alt="WULOOJ" height="28" />
          </span>
        </Link>

        <Link to={ROUTES.SA_DASHBOARD} className="logo logo-light">
          <span className="logo-sm">
            <img src={wuloojMark} alt="WULOOJ" height="22" />
          </span>
          <span className="logo-lg">
            <img src={wuloojLogoFull} alt="WULOOJ" height="28" />
          </span>
        </Link>
      </div>

      <SimpleBar className="h-100" ref={ref}>
        <div
          id="superadmin-sidebar-menu"
          className="custom_sidebar_menu custom_sidebar_menu--platform"
        >
          <ul className="metismenu list-unstyled" id={MENU_ID}>
            <SuperAdminSidebarContent t={t} />
          </ul>
        </div>
      </SimpleBar>

      <div className="sidebar-background sidebar-background--platform" />
    </div>
  )
}

export default SuperAdminSidebar
